import cx from 'classnames';
import { Checkbox } from 'react-aria-components';
import { defineMessages, FormattedMessage } from 'react-intl';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import styles from '../styles.module.css';
import type { ReactNode } from 'react';

/**
 * Link rendered inside a react-aria Checkbox label.
 * The label's press handlers would otherwise swallow the click and toggle
 * the checkbox instead of opening the link, so events are stopped here.
 */
const PrivacyLink = ({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}) => {
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  return (
    <UniversalLink
      href={href}
      openLinkInNewTab={true}
      className={styles.privacyCheckboxLink}
      onPointerDown={stop}
      onMouseDown={stop}
      onClick={stop}
      onKeyDown={stop}
    >
      {children}
    </UniversalLink>
  );
};

const PrivacySection = () => {
  return (
    <div className={styles.privacySection}>
      <Checkbox
        isRequired={true}
        className={cx('react-aria-Checkbox', styles.checkboxContainer)}
        name="privacy-policy"
      >
        <div className={styles.checkbox}>
          <svg viewBox="0 0 18 18" aria-hidden="true">
            <polyline points="1 9 7 14 15 4" />
          </svg>
        </div>
        <span>
          <FormattedMessage
            {...messages.privacy_policy_field_label}
            values={{
              privacyLink: (
                <PrivacyLink href="/privacy-policy">
                  <FormattedMessage {...messages.privacy_policy_link_label} />
                </PrivacyLink>
              ),
            }}
          />
        </span>
      </Checkbox>

      <Checkbox
        className={cx('react-aria-Checkbox', styles.checkboxContainer)}
        name="privacy-marketing"
      >
        <div className={styles.checkbox}>
          <svg viewBox="0 0 18 18" aria-hidden="true">
            <polyline points="1 9 7 14 15 4" />
          </svg>
        </div>
        <span>
          <FormattedMessage
            {...messages.marketing_field_label}
            values={{
              privacyLink: (
                <PrivacyLink href="/privacy-policy">
                  <FormattedMessage {...messages.marketing_link_label} />
                </PrivacyLink>
              ),
            }}
          />
        </span>
      </Checkbox>

      <Checkbox
        className={cx('react-aria-Checkbox', styles.checkboxContainer)}
        name="privacy-data-analysis"
      >
        <div className={styles.checkbox}>
          <svg viewBox="0 0 18 18" aria-hidden="true">
            <polyline points="1 9 7 14 15 4" />
          </svg>
        </div>
        <span>
          <FormattedMessage
            {...messages.data_field_label}
            values={{
              privacyLink: (
                <PrivacyLink href="/privacy-policy#profiling">
                  <FormattedMessage {...messages.data_link_label} />
                </PrivacyLink>
              ),
            }}
          />
        </span>
      </Checkbox>
    </div>
  );
};

export default PrivacySection;

const messages = defineMessages({
  privacy_policy_link_label: {
    id: 'privacy_policy_link_label',
    defaultMessage: 'Privacy Policy',
  },
  marketing_link_label: {
    id: 'marketing_link_label',
    defaultMessage: 'Privacy Policy',
  },
  data_link_label: {
    id: 'data_link_label',
    defaultMessage: 'Privacy Policy',
  },
  privacy_policy_field_label: {
    id: 'privacy_policy_field_label',
    defaultMessage: 'I have read and understood the {privacyLink} *',
  },
  marketing_field_label: {
    id: 'marketing_field_label',
    defaultMessage:
      'I  would like to receive news and updates from QubicaAMF (see “Marketing” purpose as defined in the {privacyLink})',
  },
  data_field_label: {
    id: 'data_field_label',
    defaultMessage:
      'I consent to the analysis of my data, interests, and interactions with promotional e-mail communications (using tracking pixels), also to receive from QubicaAMF personalized information most appropriate to my needs (see “Profiling” purpose as defined in the {privacyLink})',
  },
});
